import React, { useState, useEffect, useRef } from "react";
import { motion, useAnimation } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { useTranslation } from "react-i18next";

// Placeholder video paths - replace these with your actual assets
const VIDEO_ASSETS = ["/main.mp4", "/second.mp4", "/third.mp4"];

// const BOTTOM_TEXT_WORDS = ["Over", "600+", "adults", "transformed"];

const MainVideo: React.FC = () => {
  const { t } = useTranslation();
  const [currentVideoIndex, setCurrentVideoIndex] = useState(0);
  const controls = useAnimation();

  // Refs to control video playback manually
  const videoRefs = useRef<(HTMLVideoElement | null)[]>([]);

  //   Animation variants for the bottom "600+ adults" text
  //   const textVariants: Variants = {
  //     hidden: {
  //       opacity: 0,
  //       scale: 0.3,
  //       filter: "blur(10px)",
  //     },
  //     visible: (custom: number) => ({
  //       opacity: 1,
  //       scale: [0.3, 1.4, 1], // Keyframes for pop effect
  //       filter: ["blur(10px)", "blur(0px)", "blur(0px)"],
  //       transition: {
  //         delay: custom * 0.4,
  //         duration: 1.2,
  //         scale: {
  //           times: [0, 0.6, 1],
  //           ease: "easeOut",
  //         },
  //       },
  //     }),
  //   };

  // Trigger initial animation
  useEffect(() => {
    controls.start("visible");
  }, [controls]);

  // Handle video switching when one ends
  const handleVideoEnded = (index: number) => {
    const nextIndex = (index + 1) % VIDEO_ASSETS.length;
    setCurrentVideoIndex(nextIndex);

    const nextVideo = videoRefs.current[nextIndex];
    if (nextVideo) {
      nextVideo.currentTime = 0;
      nextVideo.play();
    }
  };

  // Replay animation on hover
  //   const handleTextHover = async () => {
  //     await controls.start("hidden");
  //     controls.start("visible");
  //   };

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-transparent">
      {/* Background Video Layer */}
      <div className="absolute inset-0 z-0 overflow-hidden">
        {VIDEO_ASSETS.map((src, index) => (
          <video
            key={index}
            //@ts-expect-error - refs array not typed
            ref={(el) => (videoRefs.current[index] = el)}
            src={src}
            autoPlay={index === 0}
            muted
            playsInline
            onEnded={() => handleVideoEnded(index)}
            className={`absolute inset-0 w-full h-full object-cover scale-110 transition-opacity duration-1000 grayscale ${
              currentVideoIndex === index ? "opacity-100" : "opacity-0"
            }`}
          />
        ))}
        {/* Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/50 to-background" />
      </div>

      {/* Main Content */}
      <div className="relative z-10 container mx-auto text-center px-4 py-32 mt-16">
        {/* Heading */}
        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          //@ts-expect-error - Tailwind CSS classes
          className="text-4xl md:text-5xl lg:text-7xl font-bold text-white leading-tight mb-6 max-w-5xl mx-auto"
        >
          {t("landingPage.mainVideo.title")}
          <br />
          <span className="inline-block text-transparent bg-clip-text bg-[#00FC00] drop-shadow-[0_0_14px_rgba(74,222,128,0.55)] [animation:pulse_2.1s_ease-in-out_infinite]">
            {t("landingPage.mainVideo.highlight")}
          </span>
        </motion.h1>

        {/* Subtitle */}
        <motion.p
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          //@ts-expect-error - Tailwind CSS classes
          className="text-lg md:text-xl text-gray-300 mb-10 max-w-2xl mx-auto"
        >
          {t("landingPage.mainVideo.subtitle")}
        </motion.p>

        {/* CTA Button */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          //@ts-expect-error - Tailwind CSS classes
          className="flex flex-col sm:flex-row items-center justify-center gap-4"
        >
          <a
            href="/book-a-call"
            target="_blank"
            rel="noopener noreferrer"
            className="btn-primary-glow !py-2.5 !px-5"
          >
            <span>{t("landingPage.mainVideo.apply")}</span>
            <span className="btn-arrow-container !w-6 !h-6">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width={24}
                height={24}
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
                className="lucide lucide-arrow-right w-3 h-3 arrow-right"
              >
                <path d="M5 12h14" />
                <path d="m12 5 7 7-7 7" />
              </svg>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width={24}
                height={24}
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
                className="lucide lucide-arrow-right w-3 h-3 arrow-left"
              >
                <path d="M5 12h14" />
                <path d="m12 5 7 7-7 7" />
              </svg>
            </span>
          </a>
          <a
            href="#method"
            className="px-8 py-4 rounded-full bg-secondary/80 backdrop-blur-sm border border-white/10 text-foreground font-semibold hover:bg-secondary transition-all"
          >
            {t("landingPage.mainVideo.watch")}
          </a>
        </motion.div>
        <p className="mt-8 text-sm text-muted-foreground max-w-lg mx-auto">
          {t("landingPage.mainVideo.extra")}
        </p>
        {/* Bottom Stats / Text */}
        {/* <div
          className="mt-10 flex items-center justify-center gap-2 md:gap-3 cursor-pointer"
          onMouseEnter={handleTextHover}
        >
          {BOTTOM_TEXT_WORDS.map((word, index) => (
            <div key={index} className="relative inline-block">
              <motion.span
                custom={index}
                initial="hidden"
                animate={controls}
                variants={textVariants}
                className="text-xl md:text-2xl lg:text-3xl font-bold text-blue-400 inline-block"
              >
                {word}
              </motion.span>
            </div>
          ))}
        </div> */}
      </div>
    </section>
  );
};

export { MainVideo };
